import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Zap, Mail, Lock, ArrowLeft, Store, KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopLogin = () => {
  const navigate = useNavigate();
  const { loginShop } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Password reset state
  const [resetStep, setResetStep] = useState(0); // 0: login, 1: request reset, 2: enter code, 3: new password
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await loginShop(formData.email, formData.password);
      toast.success('Accesso effettuato con successo!');
      navigate('/shop/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'accesso';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/auth/shop/forgot-password`, { email: resetEmail });
      toast.success('Codice di reset inviato alla tua email!');
      setResetStep(2);
    } catch (error) {
      toast.error('Errore nell\'invio del codice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/auth/shop/reset-password`, {
        email: resetEmail,
        codice: resetCode,
        new_password: newPassword
      });
      toast.success('Password aggiornata con successo!');
      setResetStep(0);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel reset della password';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderResetForm = () => {
    if (resetStep === 1) {
      // Request reset code
      return (
        <Card className="border-border/50">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <Zap className="w-8 h-8 text-primary" />
              <span className="font-heading font-bold text-xl">NIU Service</span>
            </div>
            <CardTitle className="font-heading text-2xl">Password Dimenticata</CardTitle>
            <CardDescription>
              Inserisci l'email del tuo negozio per ricevere il codice di reset
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email Negozio</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="negozio@esempio.com"
                    className="pl-10 h-12"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    data-testid="reset-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 font-bold"
                disabled={isLoading}
                data-testid="request-reset-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Invio in corso...
                  </>
                ) : 'Invia Codice Reset'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <button 
                onClick={() => setResetStep(0)} 
                className="text-primary font-medium hover:underline"
              >
                Torna al login
              </button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (resetStep === 2) {
      // Enter code and new password
      return (
        <Card className="border-border/50">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <Zap className="w-8 h-8 text-primary" />
              <span className="font-heading font-bold text-xl">NIU Service</span>
            </div>
            <CardTitle className="font-heading text-2xl">Reimposta Password</CardTitle>
            <CardDescription>
              Inserisci il codice ricevuto via email e la nuova password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-code">Codice di Verifica</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="reset-code"
                    type="text"
                    placeholder="123456"
                    className="pl-10 h-12 text-center text-2xl tracking-widest"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    required
                    data-testid="reset-code-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Nuova Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Conferma Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 font-bold"
                disabled={isLoading || resetCode.length !== 6}
                data-testid="reset-password-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Aggiornamento...
                  </>
                ) : 'Reimposta Password'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm space-y-2">
              <button 
                onClick={() => setResetStep(1)} 
                className="text-muted-foreground hover:text-foreground block w-full"
              >
                Non hai ricevuto il codice? Richiedi nuovo
              </button>
              <button 
                onClick={() => setResetStep(0)} 
                className="text-primary font-medium hover:underline"
              >
                Torna al login
              </button>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Default: Login form
    return (
      <Card className="border-border/50">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 lg:hidden mb-4">
            <Zap className="w-8 h-8 text-primary" />
            <span className="font-heading font-bold text-xl">NIU Service</span>
          </div>
          <CardTitle className="font-heading text-2xl">Accesso Negozio</CardTitle>
          <CardDescription>
            Inserisci le credenziali del tuo negozio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Negozio</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="negozio@esempio.com"
                  className="pl-10 h-12"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="shop-login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Password</Label>
                <button 
                  type="button"
                  onClick={() => setResetStep(1)} 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-12"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="shop-login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 font-bold"
              disabled={isLoading}
              data-testid="shop-login-submit-btn"
            >
              {isLoading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Non hai un account negozio? </span>
            <Link to="/shop/register" className="text-primary font-medium hover:underline" data-testid="go-to-shop-register-link">
              Registrati
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div className="hidden lg:block relative">
        <img 
          src="https://images.unsplash.com/photo-1636761358757-0a616eb9e17e?w=1200&h=1600&fit=crop"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <Store className="w-16 h-16 text-primary mb-6" />
            <h1 className="font-heading text-4xl font-black mb-4">Area Negozio</h1>
            <p className="text-white/80 text-lg">
              Gestisci le prenotazioni dei tagliandi e monitora il flusso di lavoro.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>

          {renderResetForm()}
        </div>
      </div>
    </div>
  );
};

export default ShopLogin;
